/**
 * 客户 H5/PWA 会话管理（Issue #198）。
 *
 * 与 apps/client-app 的 React Native 版本一一对应，但使用 web 版的
 * sessionStorage 替换 SecureStore。两端共享同一个 OpenAPI typed client
 * (@jinzhuan/api-client) 和同一组 /api/v1/customer-auth/* 端点。
 *
 * 红线：
 *   - 不硬编码 orgSlug / orgId / tenantId，必须由登录表单提供。
 *   - 不在前端构造 customerId / tenantId 用于业务请求；身份完全由后端从
 *     access token 派生。
 *   - 不持久化 access token；不把 refresh token 写入 localStorage。
 *   - 401 必须先尝试一次 refresh，再决定是否提示重新登录。
 */
import { ApiError, createApiClient } from '@jinzhuan/api-client';
import type { ApiComponents } from '@jinzhuan/api-client';
import { webSessionStore } from './sessionStore';

export type CustomerAccount = ApiComponents['schemas']['CustomerPublicDto'];
export type CustomerLoginRequest = ApiComponents['schemas']['CustomerLoginRequestDto'];

export interface CustomerLoginInput extends CustomerLoginRequest {
  /** Required header for /customer-auth/refresh; never inferred. */
  orgId: string;
  /** Required header for /customer-auth/refresh; in v1 tenantId === orgId. */
  tenantId: string;
}

export interface CustomerAuthenticatedSession {
  account: CustomerAccount;
}

export type CustomerAuthErrorKind =
  | 'invalid_credentials'
  | 'suspended'
  | 'session_expired'
  | 'validation'
  | 'network'
  | 'unknown';

export class CustomerAuthError extends Error {
  readonly kind: CustomerAuthErrorKind;
  constructor(kind: CustomerAuthErrorKind, message: string) {
    super(message);
    this.name = 'CustomerAuthError';
    this.kind = kind;
  }
}

type SessionExpiredListener = () => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

export function subscribeCustomerSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
}

function notifyCustomerSessionExpired(): void {
  sessionExpiredListeners.forEach((listener) => {
    listener();
  });
}

function createAnonymousAuthClient() {
  return createApiClient({ baseUrl: '' });
}

function createCustomerAuthClient() {
  return createApiClient({
    baseUrl: '',
    getAccessToken: () => webSessionStore.getAccessToken(),
    getOrgId: () => webSessionStore.loadOrgId(),
  });
}

/**
 * Create an api client bound to the customer access token (for /me/* calls).
 * Caller-owned: each hook creates its own instance per render to keep the
 * underlying middleware closures fresh.
 */
export function createCustomerMeApiClient() {
  return createApiClient({
    baseUrl: '',
    getAccessToken: () => webSessionStore.getAccessToken(),
  });
}

function normalizeLoginError(err: unknown): CustomerAuthError {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return new CustomerAuthError('invalid_credentials', '手机号、密码或组织标识不正确');
    }
    if (err.status === 403) {
      return new CustomerAuthError('suspended', '账号已停用，请联系顾问处理');
    }
    return new CustomerAuthError('unknown', '登录失败，请稍后重试');
  }
  if (err instanceof Error) {
    return new CustomerAuthError('network', '网络异常，请检查连接后重试');
  }
  return new CustomerAuthError('unknown', '登录失败，请稍后重试');
}

function trimLoginInput(input: CustomerLoginInput): CustomerLoginInput {
  return {
    orgSlug: input.orgSlug.trim(),
    orgId: input.orgId.trim(),
    tenantId: input.tenantId.trim(),
    phone: input.phone.trim(),
    password: input.password,
  };
}

export async function loginCustomerSession(
  input: CustomerLoginInput,
): Promise<CustomerAuthenticatedSession> {
  const trimmed = trimLoginInput(input);
  if (
    trimmed.orgSlug.length === 0 ||
    trimmed.orgId.length === 0 ||
    trimmed.tenantId.length === 0 ||
    trimmed.phone.length === 0 ||
    trimmed.password.length === 0
  ) {
    throw new CustomerAuthError(
      'validation',
      '请填写组织标识、组织ID、租户ID、手机号和密码',
    );
  }

  try {
    const apiClient = createAnonymousAuthClient();
    const { data } = await apiClient.POST('/api/v1/customer-auth/login', {
      body: {
        orgSlug: trimmed.orgSlug,
        phone: trimmed.phone,
        password: trimmed.password,
      },
    });

    if (data === undefined) {
      throw new CustomerAuthError('unknown', '登录响应为空，请稍后重试');
    }

    webSessionStore.setAccessToken(data.accessToken);
    webSessionStore.saveRefreshToken(data.refreshToken);
    webSessionStore.saveOrgId(trimmed.orgId);
    webSessionStore.saveTenantId(trimmed.tenantId);

    return { account: data.account };
  } catch (err: unknown) {
    if (err instanceof CustomerAuthError) {
      throw err;
    }
    throw normalizeLoginError(err);
  }
}

export async function refreshCustomerSession(): Promise<boolean> {
  const refreshToken = webSessionStore.loadRefreshToken();
  const orgId = webSessionStore.loadOrgId();
  const tenantId = webSessionStore.loadTenantId();

  if (refreshToken === null || orgId === null || tenantId === null) {
    clearCustomerSession();
    notifyCustomerSessionExpired();
    return false;
  }

  try {
    const apiClient = createCustomerAuthClient();
    const { data } = await apiClient.POST('/api/v1/customer-auth/refresh', {
      params: {
        header: {
          'X-Tenant-Id': tenantId,
          'X-Org-Id': orgId,
        },
      },
      body: { refreshToken },
    });

    if (data === undefined) {
      clearCustomerSession();
      notifyCustomerSessionExpired();
      return false;
    }

    webSessionStore.setAccessToken(data.accessToken);
    webSessionStore.saveRefreshToken(data.refreshToken);
    return true;
  } catch {
    clearCustomerSession();
    notifyCustomerSessionExpired();
    return false;
  }
}

export async function bootstrapCustomerSession(): Promise<boolean> {
  const refreshToken = webSessionStore.loadRefreshToken();
  const orgId = webSessionStore.loadOrgId();
  const tenantId = webSessionStore.loadTenantId();
  if (refreshToken === null || orgId === null || tenantId === null) {
    clearCustomerSession();
    return false;
  }
  return refreshCustomerSession();
}

export async function logoutCustomerSession(): Promise<void> {
  const refreshToken = webSessionStore.loadRefreshToken();
  if (refreshToken === null) {
    clearCustomerSession();
    return;
  }

  try {
    if (webSessionStore.getAccessToken() === null) {
      await refreshCustomerSession();
    }
    const apiClient = createCustomerAuthClient();
    await apiClient.POST('/api/v1/customer-auth/logout', {
      body: { refreshToken },
    });
  } finally {
    clearCustomerSession();
  }
}

export function clearCustomerSession(): void {
  webSessionStore.clearAll();
}
